import { Card, DataTable, PageHeader, Skeleton, type Column } from '@zyxantech/tux'
import { StatusLight } from '@zyxantech/wiz'
import { useNavigate } from 'react-router-dom'
import { useGetGatewaysQuery, type Gateway } from '@/api/gateways.api'
import { ErrorState } from '@/components/shared/ErrorState'
import { PAGINATION } from '@/constants/pagination'
import { ROUTES } from '@/constants/routes'
import { formatRelativeTime } from '@/utils/format'
import { buildPath } from '@/utils/url'
import { ProtocolTag } from './components/ProtocolTag'
import { gatewayLightStatus } from './gateway.utils'

/** KPI summary card — label + monospace count. */
function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card padding="md">
      <div className="text-xs uppercase tracking-wide text-text-muted">{label}</div>
      <div className="font-mono mt-1 text-3xl text-text-primary">{value}</div>
    </Card>
  )
}

export function GatewaysPage() {
  const navigate = useNavigate()
  // Gateways are few per deployment — fetch one large page for counts + table.
  const { data, isLoading, error, refetch } = useGetGatewaysQuery({
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
  })

  const gateways = data?.items ?? []
  const counts = {
    total: data?.pagination.total ?? gateways.length,
    online: gateways.filter((g) => g.status === 'online').length,
    offline: gateways.filter((g) => g.status === 'offline').length,
    error: gateways.filter((g) => g.status === 'error').length,
  }

  const columns: Column<Gateway>[] = [
    { key: 'name', header: 'Name', accessor: (g) => g.name, sortable: true },
    {
      key: 'protocol',
      header: 'Protocol',
      accessor: (g) => <ProtocolTag protocolConfig={g.protocolConfig} />,
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (g) => (
        <StatusLight
          status={gatewayLightStatus(g.status)}
          label={g.status}
          pulse={g.status === 'online'}
        />
      ),
      sortable: true,
      sortValue: (g) => g.status,
    },
    {
      key: 'lastSeenAt',
      header: 'Last seen',
      accessor: (g) => (
        <span className="font-mono text-xs">{formatRelativeTime(g.lastSeenAt)}</span>
      ),
    },
  ]

  return (
    <div className="p-6">
      <PageHeader title="Gateways" subtitle="Edge connectivity and protocol status" />

      {error ? (
        <ErrorState error={error} retry={() => void refetch()} />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={84} />)
            ) : (
              <>
                <StatCard label="Total" value={counts.total} />
                <StatCard label="Online" value={counts.online} />
                <StatCard label="Offline" value={counts.offline} />
                <StatCard label="Error" value={counts.error} />
              </>
            )}
          </div>

          <DataTable
            columns={columns}
            data={gateways}
            loading={isLoading}
            rowKey={(g) => g.id}
            onRowClick={(g) => navigate(buildPath(ROUTES.GATEWAY_DETAIL, { id: g.id }))}
            emptyMessage="No gateways configured yet."
          />
        </>
      )}
    </div>
  )
}
